/**
 * API client for web application
 */

export interface ApiClientConfig {
  baseUrl: string;
  timeout?: number;
  enableLogging?: boolean;
  onTokenRefresh?: (token: string) => void;
  onAuthError?: () => void;
}

export class ApiClient {
  private baseUrl: string;
  private onTokenRefresh?: (token: string) => void;
  private onAuthError?: () => void;
  private accessToken: string | null = null;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.onTokenRefresh = config.onTokenRefresh;
    this.onAuthError = config.onAuthError;
    
    // Load token from localStorage
    this.accessToken = localStorage.getItem('accessToken');
  }

  setAccessToken(token: string) {
    this.accessToken = token;
    localStorage.setItem('accessToken', token);
  }

  setRefreshToken(_token: string) {
    // Not used in simple implementation
  }

  clearTokens() {
    this.accessToken = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (this.accessToken) {
      headers.Authorization = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, try to refresh
        try {
          await this.refreshToken();
          // Retry the request
          return this.request(endpoint, options);
        } catch (error) {
          if (this.onAuthError) {
            this.onAuthError();
          }
          throw error;
        }
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  private async refreshToken(): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      this.accessToken = data.accessToken;
      localStorage.setItem('accessToken', data.accessToken);
      
      if (this.onTokenRefresh) {
        this.onTokenRefresh(data.accessToken);
      }
    } catch (error) {
      // Clear tokens and redirect to login
      this.clearTokens();
      throw error;
    }
  }

  async post(endpoint: string, data: any) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async get(endpoint: string) {
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  // Auth methods
  auth = {
    login: (credentials: { email: string; password: string }) => 
      this.post('/auth/login', credentials),
    register: (data: any) => 
      this.post('/auth/register', data),
    logout: () => 
      this.post('/auth/logout', {}),
    me: () => 
      this.get('/auth/me'),
  };

  // Services
  services = {
    getAll: () => this.get('/services'),
    getById: (id: string) => this.get(`/services/${id}`),
    create: (data: any) => this.post('/services', data),
    list: () => this.get('/services'),
    getCategories: () => this.get('/services/categories')
  };

  // Work orders
  workOrders = {
    list: (_params?: any) => this.get('/work-orders'),
  };
}
